<template>
  <div class="recursos-lista">
    <div class="page-header">
      <h1 class="page-title">
        <i class="fas fa-gavel"></i>
        Gestão de Recursos
      </h1>
      <p class="page-subtitle">Gerencie os recursos interpostos pelos candidatos</p>
    </div>

    <!-- Filtros -->
    <div class="filters-card">
      <div class="filters-row">
        <div class="filter-group">
          <label>Buscar</label>
          <input 
            v-model="filters.search" 
            type="text" 
            placeholder="Nome, CPF ou E-mail do candidato"
            class="form-control"
            @input="debounceSearch"
          />
        </div>
        
        <div class="filter-group">
          <label>Concurso</label>
          <select v-model="filters.concurso_id" class="form-control" @change="applyFilters">
            <option value="">Todos os concursos</option>
            <option v-for="concurso in concursos" :key="concurso.id" :value="concurso.id">
              {{ concurso.prefeitura }} - {{ concurso.titulo }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label>Status</label>
          <select v-model="filters.status" class="form-control" @change="applyFilters">
            <option value="">Todos</option>
            <option value="P">Pendente</option>
            <option value="D">Deferido</option>
            <option value="I">Indeferido</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Data Início</label>
          <input 
            v-model="filters.data_inicio" 
            type="date" 
            class="form-control"
            @change="applyFilters"
          />
        </div>

        <div class="filter-group">
          <label>Data Fim</label>
          <input 
            v-model="filters.data_fim" 
            type="date" 
            class="form-control"
            @change="applyFilters"
          />
        </div>

        <div class="filter-actions">
          <button @click="clearFilters" class="btn btn-secondary">
            <i class="fas fa-times"></i>
            Limpar
          </button>
          <button @click="exportData" class="btn btn-success">
            <i class="fas fa-download"></i>
            Exportar
          </button>
        </div>
      </div>
    </div>

    <!-- Estatísticas -->
    <div class="stats-cards">
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fas fa-gavel"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ estatisticas.total_recursos || 0 }}</div>
          <div class="stat-label">Total de Recursos</div>
        </div>
      </div>
      <div class="stat-card pending">
        <div class="stat-icon">
          <i class="fas fa-clock"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ estatisticas.recursos_pendentes || 0 }}</div>
          <div class="stat-label">Pendentes de Análise</div>
        </div>
      </div>
      <div class="stat-card success">
        <div class="stat-icon">
          <i class="fas fa-check-circle"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ estatisticas.recursos_deferidos || 0 }}</div>
          <div class="stat-label">Deferidos</div>
        </div>
      </div>
      <div class="stat-card danger">
        <div class="stat-icon">
          <i class="fas fa-times-circle"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ estatisticas.recursos_indeferidos || 0 }}</div>
          <div class="stat-label">Indeferidos</div>
        </div>
      </div>
    </div>

    <!-- Tabela de Recursos -->
    <div class="table-card">
      <div v-if="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Carregando recursos...</p>
      </div>

      <div v-else-if="recursos.length === 0" class="empty-state">
        <i class="fas fa-gavel"></i>
        <h3>Nenhum recurso encontrado</h3>
        <p>Não há recursos que correspondam aos filtros aplicados.</p>
      </div>

      <div v-else>
        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th @click="sortByField('id')" class="sortable">
                  ID <i class="fas fa-sort"></i>
                </th>
                <th>ID Inscrição</th>
                <th>Candidato</th>
                <th>CPF</th>
                <th>Concurso</th>
                <th>Cargo</th>
                <th>Nº Questão</th>
                <th>Data Interposição</th>
                <th>Status</th>
                <th class="actions-column">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="recurso in recursos" :key="recurso.id">
                <td>{{ recurso.id }}</td>
                <td><strong>#{{ recurso.inscricao_id || recurso.inscricao?.id || 'N/A' }}</strong></td>
                <td>
                  <div class="candidate-info">
                    <strong>{{ recurso.inscricao?.cadastro?.nome }}</strong>
                    <small class="text-muted">{{ recurso.inscricao?.cadastro?.email }}</small>
                  </div>
                </td>
                <td>{{ formatCPF(recurso.inscricao?.cadastro?.cpf) }}</td>
                <td>
                  <div class="concurso-info">
                    <strong>{{ recurso.inscricao?.concurso?.prefeitura }}</strong>
                    <small class="text-muted">{{ recurso.inscricao?.concurso?.titulo }}</small>
                  </div>
                </td>
                <td>{{ recurso.inscricao?.vaga?.cargo }}</td>
                <td>
                  <span class="badge badge-light">
                    <i class="fas fa-question-circle"></i>
                    {{ recurso.numero_questao || '-' }}
                  </span>
                </td>
                <td>{{ formatDate(recurso.created_at) }}</td>
                <td>
                  <span :class="getStatusClass(recurso.status)">
                    {{ getStatusText(recurso.status) }}
                  </span>
                </td>
                <td class="actions">
                  <button 
                    @click="viewDetails(recurso)" 
                    class="btn btn-sm btn-info"
                    title="Analisar"
                  >
                    <i class="fas fa-search"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginação -->
        <div class="pagination-wrapper">
          <nav>
            <ul class="pagination">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <button @click="changePage(currentPage - 1)" class="page-link">Anterior</button>
              </li>
              <li 
                v-for="page in visiblePages" 
                :key="page" 
                class="page-item" 
                :class="{ active: page === currentPage }"
              >
                <button @click="changePage(page)" class="page-link">{{ page }}</button>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <button @click="changePage(currentPage + 1)" class="page-link">Próxima</button>
              </li>
            </ul>
          </nav>
          <div class="pagination-info">
            Mostrando {{ (currentPage - 1) * perPage + 1 }} a {{ Math.min(currentPage * perPage, totalItems) }} de {{ totalItems }} recursos
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Análise de Recurso -->
    <div v-if="showAnaliseModal && recursoSelecionado" class="modal-overlay" @click="closeAnaliseModal">
      <div class="modal-content large" @click.stop>
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fas fa-search"></i>
            Análise de Recurso #{{ recursoSelecionado.id }}
          </h4>
          <button @click="closeAnaliseModal" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <!-- Status Atual -->
          <div class="details-section">
            <h5 class="section-title">
              <i class="fas fa-info-circle"></i>
              Status Atual
            </h5>
            <div class="details-grid">
              <div class="detail-item">
                <label>Status</label>
                <span :class="getStatusClass(recursoSelecionado.status)">
                  {{ getStatusText(recursoSelecionado.status) }}
                </span>
              </div>
              <div class="detail-item">
                <label>Data da Interposição</label>
                <span>{{ formatDate(recursoSelecionado.created_at) }}</span>
              </div>
            </div>
          </div>

          <!-- Informações do Candidato -->
          <div class="details-section">
            <h5 class="section-title">
              <i class="fas fa-user"></i>
              Dados do Candidato
            </h5>
            <div class="details-grid">
              <div class="detail-item">
                <label>Nome</label>
                <span>{{ recursoSelecionado.inscricao?.cadastro?.nome }}</span>
              </div>
              <div class="detail-item">
                <label>CPF</label>
                <span>{{ formatCPF(recursoSelecionado.inscricao?.cadastro?.cpf) }}</span>
              </div>
              <div class="detail-item">
                <label>E-mail</label>
                <span>{{ recursoSelecionado.inscricao?.cadastro?.email }}</span>
              </div>
              <div class="detail-item">
                <label>Telefone</label>
                <span>{{ recursoSelecionado.inscricao?.cadastro?.telefone_celular || '-' }}</span>
              </div>
            </div>
          </div>

          <!-- Informações do Concurso -->
          <div class="details-section">
            <h5 class="section-title">
              <i class="fas fa-clipboard-list"></i>
              Dados do Concurso
            </h5>
            <div class="details-grid">
              <div class="detail-item">
                <label>Prefeitura</label>
                <span>{{ recursoSelecionado.inscricao?.concurso?.prefeitura }}</span>
              </div>
              <div class="detail-item">
                <label>Título</label>
                <span>{{ recursoSelecionado.inscricao?.concurso?.titulo }}</span>
              </div>
              <div class="detail-item">
                <label>Cargo</label>
                <span>{{ recursoSelecionado.inscricao?.vaga?.cargo }}</span>
              </div>
              <div class="detail-item">
                <label>Nível</label>
                <span>{{ recursoSelecionado.inscricao?.vaga?.nivel || '-' }}</span>
              </div>
            </div>
          </div>

          <!-- Dados do Recurso -->
          <div class="details-section">
            <h5 class="section-title">
              <i class="fas fa-file-alt"></i>
              Recurso Interposto
            </h5>
            <div class="details-grid">
              <div class="detail-item">
                <label>Número da Questão</label>
                <span class="badge badge-primary">{{ recursoSelecionado.numero_questao || '-' }}</span>
              </div>
              <div class="detail-item">
                <label>Prazo de Recurso</label>
                <span>{{ recursoSelecionado.prazo_recurso?.nome || '-' }}</span>
              </div>
              <div class="detail-item full-width">
                <label>Motivo/Argumentação</label>
                <span>{{ recursoSelecionado.motivo }}</span>
              </div>
              <div v-if="recursoSelecionado.caminho_anexo" class="detail-item full-width">
                <label>Documento Anexado</label>
                <a :href="recursoSelecionado.caminho_anexo" target="_blank" class="link-file">
                  <i class="fas fa-file-download"></i>
                  Baixar Documento
                </a>
              </div>
            </div>
          </div>

          <!-- Formulário de Análise -->
          <div v-if="mostrarFormularioResposta" class="details-section">
            <h5 class="section-title">
              <i class="fas fa-gavel"></i>
              {{ editandoResposta ? 'Edição da Resposta' : 'Análise do Recurso' }}
            </h5>
            <div class="analise-form">
              <div class="form-group">
                <label>Decisão *</label>
                <select v-model="analiseForm.status" class="form-control">
                  <option value="">Selecione</option>
                  <option value="D">Deferir (Aprovar)</option>
                  <option value="I">Indeferir (Negar)</option>
                </select>
              </div>
              <div class="form-group">
                <label>Observação/Fundamentação</label>
                <textarea 
                  v-model="analiseForm.observacao_admin" 
                  class="form-control" 
                  rows="4"
                  placeholder="Fundamentação da decisão (opcional)"
                ></textarea>
              </div>
            </div>
          </div>

          <!-- Resultado da Análise (se já foi analisado) -->
          <div v-else class="details-section">
            <h5 class="section-title">
      <i class="fas fa-gavel"></i>
              Resultado da Análise
            </h5>
            <div class="details-grid">
              <div class="detail-item full-width">
                <label>Fundamentação do Administrador</label>
                <span>{{ recursoSelecionado.observacao_admin || 'Sem fundamentação registrada' }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeAnaliseModal" class="btn btn-secondary">
            Fechar
          </button>
          <button
            v-if="podeEditarResposta && !editandoResposta"
            @click="iniciarEdicaoResposta"
            class="btn btn-warning"
          >
            <i class="fas fa-pen"></i>
            Editar Resposta
          </button>
          <button
            v-if="editandoResposta"
            @click="cancelarEdicaoResposta"
            class="btn btn-secondary"
            :disabled="salvando"
          >
            Cancelar Edição
          </button>
          <button 
            v-if="mostrarFormularioResposta" 
            @click="salvarAnalise" 
            class="btn btn-primary"
            :disabled="!analiseForm.status || salvando"
          >
            <i v-if="salvando" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-save"></i>
            {{ salvando ? 'Salvando...' : (editandoResposta ? 'Salvar Alterações' : 'Salvar Decisão') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'RecursosLista',
  data() {
    return {
      loading: false,
      salvando: false,
      recursos: [],
      concursos: [],
      estatisticas: {},
      filters: {
        search: '',
        concurso_id: '',
        status: '',
        data_inicio: '',
        data_fim: ''
      },
      sortBy: 'created_at',
      sortOrder: 'desc',
      currentPage: 1,
      perPage: 15,
      totalItems: 0,
      totalPages: 0,
      searchTimeout: null,
      showAnaliseModal: false,
      recursoSelecionado: null,
      editandoResposta: false,
      analiseForm: {
        status: '',
        observacao_admin: ''
      }
    }
  },
  computed: {
    podeEditarResposta() {
      return !!this.recursoSelecionado && this.recursoSelecionado.status !== 'P'
    },
    mostrarFormularioResposta() {
      return !!this.recursoSelecionado && (this.recursoSelecionado.status === 'P' || this.editandoResposta)
    },
    visiblePages() {
      const pages = []
      const start = Math.max(1, this.currentPage - 2)
      const end = Math.min(this.totalPages, this.currentPage + 2)
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    }
  },
  mounted() {
    this.loadConcursos()
    this.loadRecursos()
    this.loadEstatisticas()
  },
  methods: {
    async loadConcursos() {
      try {
        const response = await axios.get('/ps/concurso')
        this.concursos = response.data
      } catch (error) {
        console.error('Erro ao carregar concursos:', error)
      }
    },

    async loadRecursos() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
          sort_by: this.sortBy,
          sort_order: this.sortOrder,
          ...this.filters
        }

        const response = await axios.get('/ps/recurso/admin/todos', { params })
        this.recursos = response.data.data
        this.totalItems = response.data.total
        this.totalPages = response.data.last_page
      } catch (error) {
        console.error('Erro ao carregar recursos:', error)
        this.$sweet.showError('Erro ao carregar recursos')
      } finally {
        this.loading = false
      }
    },

    async loadEstatisticas() {
      try {
        const params = { ...this.filters }
        const response = await axios.get('/ps/recurso/admin/estatisticas', { params })
        this.estatisticas = response.data
      } catch (error) {
        console.error('Erro ao carregar estatísticas:', error)
      }
    },
    
    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.applyFilters()
      }, 500)
    },

    applyFilters() {
      this.currentPage = 1
      this.loadRecursos()
      this.loadEstatisticas()
    },

    clearFilters() {
      this.filters = {
        search: '',
        concurso_id: '',
        status: '',
        data_inicio: '',
        data_fim: ''
      }
      this.currentPage = 1
      this.loadRecursos()
      this.loadEstatisticas()
    },

    sortByField(field) {
      if (this.sortBy === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = field
        this.sortOrder = 'asc'
      }
      this.loadRecursos()
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        this.loadRecursos()
      }
    },

    async viewDetails(recurso) {
      this.recursoSelecionado = recurso
      this.showAnaliseModal = true
      this.editandoResposta = false
      
      // Resetar formulário
      this.analiseForm = {
        status: '',
        observacao_admin: ''
      }

      // Carregar dados completos se necessário
      if (!recurso.detalhes_carregados) {
        await this.loadRecursoDetalhes(recurso.id)
      }
    },

    async loadRecursoDetalhes(recursoId) {
      try {
        const response = await axios.get(`/ps/recurso/admin/${recursoId}`)
        this.recursoSelecionado = {
          ...this.recursoSelecionado,
          ...response.data,
          detalhes_carregados: true
        }
      } catch (error) {
        console.error('Erro ao carregar detalhes do recurso:', error)
        this.$sweet.showError('Erro ao carregar detalhes do recurso')
      }
    },

    closeAnaliseModal() {
      this.showAnaliseModal = false
      this.recursoSelecionado = null
      this.editandoResposta = false
      this.analiseForm = {
        status: '',
        observacao_admin: ''
      }
    },

    iniciarEdicaoResposta() {
      if (!this.recursoSelecionado) return
      this.editandoResposta = true
      this.analiseForm = {
        status: this.recursoSelecionado.status || '',
        observacao_admin: this.recursoSelecionado.observacao_admin || ''
      }
    },

    cancelarEdicaoResposta() {
      this.editandoResposta = false
      this.analiseForm = {
        status: '',
        observacao_admin: ''
      }
    },

    async salvarAnalise() {
      if (!this.analiseForm.status) {
        this.$sweet.showWarning('Selecione uma decisão')
        return
      }

      const titulo = this.editandoResposta ? 'Confirmar Edição' : 'Confirmar Decisão'
      const acao = this.analiseForm.status === 'D' ? 'deferir' : 'indeferir'
      const descricao = this.editandoResposta
        ? `Tem certeza que deseja <b>alterar</b> a decisão deste recurso para <b>${acao}</b>?`
        : `Tem certeza que deseja ${acao} este recurso?`

      const { value } = await this.$sweet.showConfirm(titulo, descricao)

      if (!value) return

      this.salvando = true
      try {
        await axios.put(`/ps/recurso/resposta/${this.recursoSelecionado.id}`, this.analiseForm)
        
        const msg = this.editandoResposta ? 'Resposta atualizada com sucesso!' : 'Decisão registrada com sucesso!'
        this.$sweet.showSuccess(msg)

        // Atualizar recurso local (para refletir no modal e na listagem imediatamente)
        this.recursoSelecionado = {
          ...this.recursoSelecionado,
          status: this.analiseForm.status,
          observacao_admin: this.analiseForm.observacao_admin
        }
        this.editandoResposta = false

        // Recarregar lista/estatísticas para garantir consistência
        this.loadRecursos()
        this.loadEstatisticas()
      } catch (error) {
        console.error('Erro ao salvar análise:', error)
        this.$sweet.showError('Erro ao salvar análise')
      } finally {
        this.salvando = false
      }
    },

    async exportData() {
      try {
        this.$sweet.showInfo('Gerando arquivo Excel...')
        
        const response = await axios.get('/ps/recurso/admin/exportar-excel', {
          params: this.filters,
          responseType: 'blob'
        })
        
        // Criar download do arquivo
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `recursos_${new Date().getTime()}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        this.$sweet.showSuccess('Arquivo exportado com sucesso!')
      } catch (error) {
        console.error('Erro ao exportar dados:', error)
        this.$sweet.showError('Erro ao exportar dados')
      }
    },

    formatCPF(cpf) {
      if (!cpf) return '-'
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    },

    formatDate(date) {
      if (!date) return '-';
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = date.split('-');
          return `${day}/${month}/${year}`;
        }
        
        // Tratar formato do banco: YYYY-MM-DD HH:mm:ss (com espaço)
        if (typeof date === 'string') {
          let dateString = date.replace('Z', '');
          
          // Se tiver formato com espaço (formato do banco: YYYY-MM-DD HH:mm:ss)
          if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/)) {
            const [datePart] = dateString.split(' ');
            const [year, month, day] = datePart.split('-');
            return `${day}/${month}/${year}`;
          }
          
          // Para datas ISO com timezone, remover o 'Z' e tratar como local
          if (dateString.includes('T')) {
            dateString = dateString.replace('Z', '');
            if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
              const [datePart] = dateString.split('T');
              const [year, month, day] = datePart.split('-');
              return `${day}/${month}/${year}`;
            }
          }
        }
        
        // Criar data e usar métodos locais
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return '-';
        
        const dia = String(dateObj.getDate()).padStart(2, '0');
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
        const ano = dateObj.getFullYear();
        return `${dia}/${mes}/${ano}`;
      } catch (error) {
        return '-';
      }
    },

    getStatusClass(status) {
      return {
        'badge badge-warning': status === 'P',
        'badge badge-success': status === 'D',
        'badge badge-danger': status === 'I'
      }
    },

    getStatusText(status) {
      const statuses = {
        'P': 'Pendente',
        'D': 'Deferido',
        'I': 'Indeferido'
      }
      return statuses[status] || status
    }
  }
}
</script>

<style lang="scss" scoped>
// Reutilizando os mesmos estilos da view de isenções
.recursos-lista {
  .page-header {
    background: white;
    border-radius: 15px;
    padding: 25px 30px;
    margin-bottom: 20px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);

    .page-title {
      margin: 0 0 8px 0;
      font-size: 24px;
      font-weight: 600;
      color: #2c3e50;
      display: flex;
      align-items: center;
      gap: 10px;

      i {
      color: #667eea;
      }
    }

    .page-subtitle {
      margin: 0;
      font-size: 14px;
      color: #6c757d;
    }
  }

  .filters-card {
    background: white;
    border-radius: 15px;
    padding: 20px 30px;
    margin-bottom: 20px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);

    .filters-row {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 20px;
      align-items: end;

      .filter-group {
        label {
          display: block;
          margin-bottom: 5px;
          font-weight: 600;
          color: #495057;
          font-size: 14px;
        }

        .form-control {
          width: 100%;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 8px 12px;
          font-size: 14px;
          transition: border-color 0.3s ease;

          &:focus {
            outline: none;
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          }
        }
      }

      .filter-actions {
        display: flex;
        gap: 10px;
        grid-column: 1 / -1;
        justify-content: center;
        margin-top: 10px;

        .btn {
          padding: 8px 16px;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          cursor: pointer;

          &.btn-secondary {
            background: #6c757d;
            color: white;

            &:hover {
              background: #5a6268;
            }
          }

          &.btn-success {
            background: #28a745;
            color: white;

            &:hover {
              background: #218838;
            }
          }
        }
      }
    }
  }

  .stats-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
      margin-bottom: 20px;

    .stat-card {
      background: white;
      border-radius: 15px;
      padding: 25px;
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
      border: 1px solid rgba(0, 0, 0, 0.05);
      display: flex;
      align-items: center;
      gap: 20px;

      .stat-icon {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 24px;
      }

      &.pending .stat-icon {
        background: linear-gradient(135deg, #ffc107 0%, #ff9800 100%);
      }

      &.success .stat-icon {
        background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
      }

      &.danger .stat-icon {
        background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
      }

      .stat-content {
        .stat-value {
          font-size: 28px;
          font-weight: 700;
      color: #2c3e50;
          margin-bottom: 5px;
        }

        .stat-label {
          font-size: 14px;
          color: #6c757d;
          font-weight: 500;
        }
      }
    }
  }

  .table-card {
    background: white;
    border-radius: 15px;
    padding: 0;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    overflow: hidden;

    .loading-state, .empty-state {
      padding: 60px 30px;
      text-align: center;

      i {
        font-size: 48px;
        color: #667eea;
        margin-bottom: 15px;
        opacity: 0.7;
      }

      p, h3 {
      margin: 0;
      color: #6c757d;
      }

      h3 {
        font-size: 20px;
        font-weight: 600;
        margin-bottom: 8px;
        color: #495057;
      }
    }

    .table-responsive {
      overflow-x: auto;
    }

    .table {
      margin: 0;
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;

      thead {
        background: #f8f9fa;

        th {
          padding: 15px 12px;
          font-weight: 600;
          color: #495057;
          border-bottom: 2px solid #e9ecef;
          font-size: 14px;
          white-space: nowrap;

          &.sortable {
            cursor: pointer;
            user-select: none;
            transition: background-color 0.3s ease;

            &:hover {
              background: #e9ecef;
            }

            i {
              margin-left: 5px;
              opacity: 0.5;
            }
          }
        }
      }

      tbody {
        tr {
          transition: background-color 0.3s ease;

          &:hover {
            background: #f8f9fa;
          }

          td {
            padding: 12px;
            border-bottom: 1px solid #e9ecef;
            vertical-align: middle;
            font-size: 14px;

            .candidate-info, .concurso-info {
              strong {
                color: #2c3e50;
                display: block;
                margin-bottom: 2px;
                line-height: 1.3;
              }

              small {
                color: #6c757d;
                font-size: 11px;
                line-height: 1.2;
                max-height: 32px;
                overflow: hidden;
                text-overflow: ellipsis;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
              }
            }

            .badge {
              font-size: 12px;
              padding: 4px 8px;
              border-radius: 12px;
              font-weight: 500;

              &.badge-warning {
                background: #ffc107;
                color: #212529;
              }

              &.badge-success {
                background: #28a745;
                color: white;
              }

              &.badge-danger {
                background: #dc3545;
                color: white;
              }

              &.badge-light {
                background: #f8f9fa;
                color: #495057;
                border: 1px solid #e9ecef;
              }

              &.badge-primary {
                background: #667eea;
                color: white;
              }
            }

            &.actions {
              text-align: center;
              white-space: nowrap;

              .btn {
                margin: 0 2px;
                padding: 6px 10px;
                border-radius: 6px;
                font-size: 12px;
                border: none;
                transition: all 0.3s ease;
                cursor: pointer;

                &.btn-info {
                  background: #17a2b8;
                  color: white;

                  &:hover {
                    background: #138496;
                  }
                }
              }
            }
          }
        }
      }
    }

    .pagination-wrapper {
      padding: 20px 30px;
      border-top: 1px solid #e9ecef;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #f8f9fa;

      .pagination {
        margin: 0;
        padding: 0;
        list-style: none;
        display: flex;
        gap: 5px;

        .page-item {
          .page-link {
            padding: 8px 12px;
            border: 1px solid #e9ecef;
            color: #667eea;
            background: white;
            border-radius: 6px;
            transition: all 0.3s ease;
            cursor: pointer;

            &:hover {
              background: #667eea;
              color: white;
              border-color: #667eea;
            }
          }

          &.active .page-link {
            background: #667eea;
            color: white;
            border-color: #667eea;
          }

          &.disabled .page-link {
            color: #6c757d;
            background: #f8f9fa;
            cursor: not-allowed;

            &:hover {
              background: #f8f9fa;
              color: #6c757d;
              border-color: #e9ecef;
            }
          }
        }
      }

      .pagination-info {
        font-size: 14px;
        color: #6c757d;
      }
    }
  }

  // Modal Styles
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    padding: 20px;
  }

  .modal-content {
    background: white;
    border-radius: 15px;
    width: 100%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);

    &.large {
      max-width: 900px;
    }

    .modal-header {
      padding: 20px 25px;
      border-bottom: 1px solid #e9ecef;
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: #f8f9fa;

      .modal-title {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: #2c3e50;
        display: flex;
        align-items: center;
        gap: 10px;

        i {
          color: #667eea;
        }
      }

      .modal-close {
        background: none;
        border: none;
        font-size: 20px;
        color: #6c757d;
        cursor: pointer;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: all 0.3s ease;

        &:hover {
          background: #e9ecef;
          color: #495057;
        }
      }
    }

    .modal-body {
      padding: 25px;

      .details-section {
        margin-bottom: 25px;
        padding-bottom: 25px;
        border-bottom: 1px solid #e9ecef;

        &:last-child {
          border-bottom: none;
          margin-bottom: 0;
          padding-bottom: 0;
        }

        .section-title {
          margin: 0 0 15px 0;
          font-size: 16px;
          font-weight: 600;
          color: #495057;
          display: flex;
          align-items: center;
          gap: 8px;

          i {
            color: #667eea;
            font-size: 14px;
          }
        }

        .details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;

          .detail-item {
            &.full-width {
              grid-column: 1 / -1;
            }

            label {
              display: block;
              font-weight: 600;
              color: #6c757d;
              font-size: 13px;
              margin-bottom: 5px;
            }

            span {
              display: block;
              color: #2c3e50;
              font-size: 14px;
            }

            .link-file {
              color: #667eea;
              text-decoration: none;
              display: inline-flex;
              align-items: center;
              gap: 5px;
              font-size: 14px;
              transition: all 0.3s ease;

              &:hover {
                color: #5568d3;
                text-decoration: underline;
              }

              i {
                font-size: 12px;
              }
            }
          }
        }

        .analise-form {
          .form-group {
            margin-bottom: 15px;

            label {
              display: block;
              font-weight: 600;
              color: #495057;
              margin-bottom: 8px;
              font-size: 14px;
            }

            .form-control {
              width: 100%;
              padding: 10px 12px;
              border: 2px solid #e9ecef;
              border-radius: 8px;
              font-size: 14px;
              transition: border-color 0.3s ease;

              &:focus {
                outline: none;
                border-color: #667eea;
                box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
              }
            }

            textarea.form-control {
              resize: vertical;
              min-height: 100px;
            }
          }
        }
      }
    }

    .modal-footer {
      padding: 15px 25px;
      border-top: 1px solid #e9ecef;
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      background: #f8f9fa;

      .btn {
        padding: 10px 20px;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 8px;

        &.btn-secondary {
          background: #6c757d;
          color: white;

          &:hover {
            background: #5a6268;
          }
        }

        &.btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;

          &:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
          }

          &:disabled {
            background: #6c757d;
            cursor: not-allowed;
            transform: none;
          }
        }

        &.btn-warning {
          background: #ffc107;
          color: #212529;

          &:hover:not(:disabled) {
            background: #e0a800;
          }
        }
      }
    }
  }
}

// Responsividade
@media (max-width: 768px) {
  .recursos-lista {
    .filters-card .filters-row {
      grid-template-columns: 1fr;
    }

    .stats-cards {
      grid-template-columns: 1fr;
    }

    .table-card .pagination-wrapper {
      flex-direction: column;
      gap: 15px;
      text-align: center;
    }
  }
}
</style>
